import React from "react";
import { Clock, Mail, Phone, Github, Linkedin, Globe } from "lucide-react";

interface Experience {
    role: string;
    company: string;
    startDate: string;
    endDate: string;
    present: boolean;
    description: string;
}

interface Education {
    institution: string;
    degree: string;
    fieldOfStudy: string;
    duration: string;
}

interface Certification {
    name: string;
    issuingOrganization: string;
    date: string;
}

interface Profile {
    ID: string;
    UserID: string;
    Name: string;
    Email: string;
    Phone: string;
    Linkedin: string;
    Github: string;
    Website: string;
    Summary: string;
    Experience: string;
    Education: string;
    Skills: string;
    Certifications: string;
    CreatedAt: string;
    ProfilePicture: string | null;
}

interface ResumeProps {
    profile: Profile;
}

const Resume: React.FC<ResumeProps> = ({ profile }) => {
    const parsedExperience = JSON.parse(profile.Experience || '[]') as Experience[];
    const parsedEducation = JSON.parse(profile.Education || '[]') as Education[];
    const parsedSkills = JSON.parse(profile.Skills || '[]') as string[];
    const parsedCertifications = JSON.parse(profile.Certifications || '[]') as Certification[];

    const SectionTitle = ({ children }: { children: React.ReactNode }) => (
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center border-b border-gray-200 pb-2">
            {children}
        </h2>
    );

    return (
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-xl pt-8 pb-8">
            <div className="p-8">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">{profile.Name}</h1>
                            <div className="space-y-2">
                                <div className="flex items-center text-gray-600">
                                    <Mail className="h-4 w-4 mr-2" />
                                    <span>{profile.Email}</span>
                                </div>
                                {profile.Phone && (
                                    <div className="flex items-center text-gray-600">
                                        <Phone className="h-4 w-4 mr-2" />
                                        <span>{profile.Phone}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-4">
                            {profile.Github && (
                                <a href={profile.Github} className="text-gray-600 hover:text-gray-900 transition-colors">
                                    <Github className="h-6 w-6" />
                                </a>
                            )}
                            {profile.Linkedin && (
                                <a href={profile.Linkedin} className="text-gray-600 hover:text-gray-900 transition-colors">
                                    <Linkedin className="h-6 w-6" />
                                </a>
                            )}
                            {profile.Website && (
                                <a href={profile.Website} className="text-gray-600 hover:text-gray-900 transition-colors">
                                    <Globe className="h-6 w-6" />
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {/* Summary Section */}
                {profile.Summary && (
                    <div className="mb-8">
                        <SectionTitle>Professional Summary</SectionTitle>
                        <p className="text-gray-600 leading-relaxed">{profile.Summary}</p>
                    </div>
                )}

                {/* Experience Section */}
                {parsedExperience.length > 0 && (
                    <div className="mb-8">
                        <SectionTitle>Professional Experience</SectionTitle>
                        <div className="space-y-6">
                            {parsedExperience.map((exp, index) => (
                                <div key={index} className="group relative pl-4 border-l-2 border-gray-200 hover:border-blue-500 transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">{exp.role}</h3>
                                            <p className="text-blue-600 font-medium">{exp.company}</p>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-500">
                                            <Clock className="h-4 w-4 mr-1" />
                                            {new Date(exp.startDate).toLocaleDateString('en-US', {
                                                month: 'long',
                                                year: 'numeric'
                                            })} - {
                                                exp.present ? 'Present' :
                                                    new Date(exp.endDate).toLocaleDateString('en-US', {
                                                        month: 'long',
                                                        year: 'numeric'
                                                    })
                                            }
                                        </div>
                                    </div>
                                    {exp.description && (
                                        <p className="text-gray-600 leading-relaxed">{exp.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-8">
                    {/* Education Section */}
                    {parsedEducation.length > 0 && (
                        <div>
                            <SectionTitle>Education</SectionTitle>
                            <div className="space-y-4">
                                {parsedEducation.map((edu, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-semibold text-gray-900">{edu.institution}</h3>
                                        <p className="text-blue-600">{edu.degree}</p>
                                        <p className="text-gray-600">{edu.fieldOfStudy}</p>
                                        <p className="text-sm text-gray-500 mt-1">{edu.duration}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Certifications Section */}
                    {parsedCertifications.length > 0 && (
                        <div>
                            <SectionTitle>Certifications</SectionTitle>
                            <div className="space-y-4">
                                {parsedCertifications.map((cert, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-semibold text-gray-900">{cert.name}</h3>
                                        <p className="text-blue-600">{cert.issuingOrganization}</p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {new Date(cert.date).toLocaleDateString('en-US', {
                                                month: 'long',
                                                year: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Skills Section */}
                {parsedSkills.length > 0 && (
                    <div className="mt-8 mb-8">
                        <SectionTitle>Skills & Technologies</SectionTitle>
                        <div className="flex flex-wrap gap-2">
                            {parsedSkills.map((skill, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                >
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Resume;